<script setup lang="ts">
import type { CoverArtPresignBody } from '@@/types/api/common';
import type { CoverArtPresignResult } from '~/composables/use-cover-art';
import { useEventIdeasApi } from '~/composables/api/use-event-ideas-api';
import { useEventIdeaUploadsApi } from '~/composables/api/use-event-idea-uploads-api';
import { useAuthStore } from '~/stores/use-auth-store';
import { useIdeaFormStore } from '~/stores/use-idea-form-store';
import { useMatomo } from '~/composables/use-matomo';
import { MAX_ACTIVE_IDEAS, countActiveIdeas } from '~/utils/idea-policy';

definePageMeta({
  layout: 'dashboard',
  middleware: 'auth',
});

useHead({
  title: 'Float an Idea | Arvee Events',
});

const store = useIdeaFormStore();
const ideasApi = useEventIdeasApi();
const authStore = useAuthStore();
const { trackEvent } = useMatomo();

const ideaForm = ref<{
  validate: () => Promise<{ valid: boolean }>;
  uploadCoverArt: () => Promise<{ success: boolean; error?: string }>;
} | null>(null);

const submitError = ref<string | null>(null);
const completed = ref(false);

// Set the moment the POST succeeds — the presign closure needs it, and it is
// what permanently locks the form against a second POST.
const createdIdeaId = ref<string | null>(null);

// --- Guards (both courtesies; the backend is authoritative) ----------------

// The backend's `verified` middleware 403s an unverified author before the
// controller ever runs. `user` is null during SSR, so the gate only engages
// once a real profile is in hand — otherwise every SSR paint would flash it.
const showVerifyGate = computed(
  () => authStore.user !== null && !authStore.user.email_verified_at,
);

const activeIdeaCount = ref<number | null>(null);
const atCap = computed(
  () =>
    activeIdeaCount.value !== null && activeIdeaCount.value >= MAX_ACTIVE_IDEAS,
);

// Once the idea exists the form must never POST again. In practice we navigate
// away immediately, but the lock covers the window while the cover uploads.
const formDisabled = computed(
  () => atCap.value || createdIdeaId.value !== null,
);

onMounted(async () => {
  store.$reset();
  trackEvent('Idea', 'CreateStarted');

  try {
    const mine = await ideasApi.myIdeas({ per_page: 100 });
    activeIdeaCount.value = countActiveIdeas(mine.data);
  } catch {
    // A failed count is not a reason to block authoring — the backend's 422
    // still catches the cap on submit.
    activeIdeaCount.value = null;
  }
});

onBeforeUnmount(() => {
  if (!completed.value) {
    trackEvent('Idea', 'CreateAbandoned', store.timingKind);
  }
  store.$reset();
});

// --- Cover-art transport ---------------------------------------------------

/**
 * Idea cover-art presign. The mirror image of the native event flow: there the
 * presign CREATES the draft and the id is written back through `commit`; here
 * the idea must already exist, so the id is read from `createdIdeaId` and there
 * is nothing to write back — hence no `commit`.
 */
async function presignIdeaCover(
  body: CoverArtPresignBody,
): Promise<CoverArtPresignResult> {
  const ideaId = createdIdeaId.value;
  if (!ideaId) {
    throw new Error('The idea must be created before its cover can upload.');
  }
  const res = await useEventIdeaUploadsApi(ideaId).getPresignedUrl(body);
  return {
    presigned_url: res.presigned_url,
    media_asset_id: res.media_asset_id,
  };
}

// --- Submit ----------------------------------------------------------------

function applyApiError(e: unknown): void {
  if (e && typeof e === 'object' && 'name' in e && e.name === 'ApiError') {
    const apiErr = e as unknown as {
      status: number;
      message: string;
      validation?: Record<string, string[]>;
    };
    if (apiErr.status === 422 && apiErr.validation) {
      store.serverErrors = apiErr.validation;
      submitError.value = 'Please fix the highlighted errors and try again.';
      return;
    }
    // The 5-active cap is a bare `{ message }` 422 with NO errors bag, and the
    // unverified-email rejection is a 403 — both are page-level, not field-level.
    submitError.value = apiErr.message;
    return;
  }
  submitError.value = 'An unexpected error occurred. Please try again.';
}

/**
 * Arrival signal for the idea detail page.
 *
 *   `?created=1`               → the success banner
 *   `?created=1&cover=failed`  → the cover-art recovery warning instead
 *
 * The detail page reads these on mount and immediately strips them from the
 * URL, so a refresh, a back-nav, or a reshared link never replays the banner.
 */
function arrivalPath(ideaId: string, coverFailed: boolean): string {
  return coverFailed
    ? `/ideas/${ideaId}?created=1&cover=failed`
    : `/ideas/${ideaId}?created=1`;
}

async function handleSubmit() {
  if (store.isSubmitting || formDisabled.value) return;

  submitError.value = null;
  store.serverErrors = {};

  const localErrors = store.validateLocal();
  if (Object.keys(localErrors).length > 0) {
    store.serverErrors = localErrors;
    submitError.value = 'Please fix the highlighted errors and try again.';
    return;
  }

  // Held for the WHOLE sequence (POST → presign → S3 PUT) so a double-click
  // can't create two ideas.
  store.isSubmitting = true;

  let ideaId: string;
  try {
    const idea = await ideasApi.create(store.buildCreatePayload());
    ideaId = idea.id;
    createdIdeaId.value = idea.id;
    trackEvent('Idea', 'Created', idea.id);
  } catch (e: unknown) {
    // Genuine failure: no idea exists. Stay on the form and let `IdeaForm`
    // render the message next to the submit button.
    applyApiError(e);
    store.isSubmitting = false;
    return;
  }

  // Past this point the idea EXISTS, and the form's responsibility is over.
  // Whatever the cover art does, we navigate — a failed cover is recoverable
  // detail-page state, not a reason to strand the author on a form that can
  // never legitimately submit again. `isSubmitting` deliberately stays true
  // through the navigation (the unmount hook resets the store).
  let coverFailed = false;
  try {
    const uploadResult = await ideaForm.value?.uploadCoverArt();
    coverFailed = uploadResult ? !uploadResult.success : false;
  } catch {
    coverFailed = true;
  }

  completed.value = true;
  await navigateTo(arrivalPath(ideaId, coverFailed));
}
</script>

<template>
  <div style="max-width: 760px">
    <h1 class="text-h4 mb-1">Float an idea</h1>
    <p class="text-body-2 text-medium-emphasis mb-4">
      An idea is a half-formed pitch, not a scheduled event. Sketch it out and
      see who's interested before you commit to organizing anything.
    </p>

    <!-- Email-verified gate — the backend 403s unverified authors outright.
         The gate resolves IN PLACE: it sends and checks the OTP itself, then
         refreshes the profile, which flips `showVerifyGate` and reveals the
         form below without a reload. -->
    <AuthEmailVerificationGate
      v-if="showVerifyGate"
      title="Verify your email to float an idea"
      reason="Ideas are attributed to a real person, so floating one needs a verified email address."
    />

    <template v-else>
      <v-alert
        v-if="atCap"
        type="info"
        variant="tonal"
        class="mb-4"
        data-testid="idea-cap-alert"
      >
        You already have {{ MAX_ACTIVE_IDEAS }} active ideas. Withdraw one from
        <NuxtLink to="/dashboard/ideas">My Ideas</NuxtLink> before floating
        another.
      </v-alert>

      <!-- The submit error lives INSIDE the form, next to the button that
           produced it — see IdeaForm. -->
      <IdeasFormIdeaForm
        ref="ideaForm"
        :presign="presignIdeaCover"
        :disabled="formDisabled"
        :submit-error="submitError"
        submit-label="Float this idea"
        @submit="handleSubmit"
        @clear-error="submitError = null"
      />
    </template>
  </div>
</template>
